"use client";

import { useEffect, useMemo, useState } from "react";
import { Info, Loader2, Search, X } from "lucide-react";
import type { Agent } from "@/lib/api";
import { api } from "@/lib/api";
import { useCartesiaAudioPlayer, VoicePlayButton } from "@/components/voice-play-button";
import { Switch } from "@/components/ui/switch";

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
};

type ElVoice = { voiceId: string; name: string; previewUrl?: string | null };

function voiceTitle(v: ElVoice): string {
  return String(v.name || "").trim() || `Voice ${(v.voiceId || "").slice(0, 8) || "—"}`;
}

function clampLaSpeed(v: unknown, fallback = 0.9): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.round(Math.max(0.8, Math.min(1.2, n)) * 100) / 100;
}

function clampUnit(v: unknown, fallback: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.round(Math.max(0, Math.min(1, n)) * 1000) / 1000;
}

const LA_VOICE_MODELS = [
  { id: "eleven_flash_v2_5", label: "Flash v2.5 (default, lower latency)" },
  { id: "eleven_multilingual_v2", label: "Multilingual v2 (often steadier tone)" },
] as const;

function resolveLaVoiceModel(draft: Agent): string {
  const raw = String(draft.liveAvatarVoiceModel || "eleven_flash_v2_5").trim();
  return raw === "eleven_multilingual_v2" ? raw : "eleven_flash_v2_5";
}

/**
 * LiveAvatar Voice tab — same selected-card + library layout as Cascade
 * (`AgentEditVoicePanel`), backed by ElevenLabs instead of Cartesia.
 */
export function AgentEditLiveAvatarVoicePanel({ draft, onChange }: Props) {
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [voices, setVoices] = useState<ElVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const player = useCartesiaAudioPlayer();

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  useEffect(() => {
    const t = window.setTimeout(() => setQDebounced(q.trim()), 280);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setListError(null);
      try {
        const res = await api.listElevenLabsVoices();
        if (cancelled) return;
        setVoices(Array.isArray(res?.voices) ? res.voices : []);
      } catch (e) {
        if (!cancelled) {
          setListError(e instanceof Error ? e.message : "Could not load ElevenLabs voices");
          setVoices([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      player.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount load only
  }, []);

  const selectedId = String(draft.elevenLabsVoiceId || "").trim();
  const selected = voices.find((v) => v.voiceId === selectedId) || null;
  const displayName = selected
    ? voiceTitle(selected)
    : selectedId
      ? `Voice ${selectedId.slice(0, 8)}`
      : "No voice selected";
  const displayDesc = selectedId
    ? "Selected ElevenLabs voice for LiveAvatar FULL."
    : "Pick an ElevenLabs voice below. Pulse imports it into LiveAvatar on Save.";

  const filtered = useMemo(() => {
    const needle = qDebounced.toLowerCase();
    if (!needle) return voices;
    return voices.filter(
      (v) =>
        v.name.toLowerCase().includes(needle) ||
        v.voiceId.toLowerCase().includes(needle),
    );
  }, [voices, qDebounced]);

  const selectedPlayKey = selectedId ? `el:${selectedId}` : "";
  const selectedPlayState = selectedPlayKey
    ? player.stateFor(selectedPlayKey)
    : "idle";

  return (
    <div className={`agent-voice${libraryOpen ? " has-library" : ""}`}>
      <div className="agent-voice-main">
        <div className="agent-eot-title-row">
          <div>
            <div className="agent-voice-title-line">
              <h3 className="agent-conv-h">Voice</h3>
              <span
                className="agent-voice-autosave-hint"
                title="Voice fields save with Save configuration"
              >
                <Info size={12} aria-hidden />
                Saves with configuration
              </span>
            </div>
            <p className="agent-conv-guards-lead">
              LiveAvatar FULL owns recognition and replies. Choose the ElevenLabs voice
              heard in the call (not Cartesia, not GPT-Live).
            </p>
          </div>
          {!libraryOpen && (
            <button
              type="button"
              className="agent-guards-tips-link"
              onClick={() => setLibraryOpen(true)}
            >
              Choose a voice →
            </button>
          )}
        </div>

        <section className="agent-limits-card">
          <header className="agent-limits-card-head">
            <div>
              <h3 className="agent-conv-h">Speaking speed</h3>
              <p className="agent-limits-lead">
                ElevenLabs pace on new calls (0.8 = slower, 1.2 = faster). Applied when the session
                starts — Save, then Publish before testing.
              </p>
            </div>
          </header>
          <label className="agent-limits-slider">
            <div className="agent-limits-slider-head">
              <b>Speed</b>
              <span className="agent-limits-range">
                {clampLaSpeed(draft.liveAvatarVoiceSpeed ?? draft.ttsSpeed ?? 0.9, 0.9)}
              </span>
            </div>
            <div className="agent-limits-slider-row">
              <input
                type="range"
                min={0.8}
                max={1.2}
                step={0.05}
                value={clampLaSpeed(draft.liveAvatarVoiceSpeed ?? draft.ttsSpeed ?? 0.9, 0.9)}
                onChange={(e) =>
                  patch({ liveAvatarVoiceSpeed: clampLaSpeed(e.target.value, 0.9) })
                }
                aria-label="LiveAvatar speaking speed"
              />
            </div>
          </label>
        </section>

        <section className="agent-limits-card">
          <header className="agent-limits-card-head">
            <div>
              <h3 className="agent-conv-h">ElevenLabs tuning</h3>
              <p className="agent-limits-lead">
                Passed in <code>voice_settings</code> on each new LiveAvatar FULL session. Higher style adds
                expressiveness; speaker boost can add edge-case artifacts on some voices.
              </p>
            </div>
          </header>

          <label className="agent-limits-slider">
            <div className="agent-limits-slider-head">
              <b>Style</b>
              <span className="agent-limits-range">
                {clampUnit(draft.liveAvatarVoiceStyle, 0)}
              </span>
            </div>
            <div className="agent-limits-slider-row">
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={clampUnit(draft.liveAvatarVoiceStyle, 0)}
                onChange={(e) =>
                  patch({ liveAvatarVoiceStyle: clampUnit(e.target.value, 0) })
                }
                aria-label="LiveAvatar voice style"
              />
            </div>
          </label>

          <label className="agent-conv-field" style={{ marginTop: 16 }}>
            <span className="agent-conv-label">TTS model</span>
            <select
              className="agent-conv-input"
              value={resolveLaVoiceModel(draft)}
              onChange={(e) => patch({ liveAvatarVoiceModel: e.target.value })}
              aria-label="ElevenLabs model for LiveAvatar"
            >
              {LA_VOICE_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          <div
            className="agent-limits-card-head"
            style={{ marginTop: 18, alignItems: "center", flexDirection: "row", gap: 12 }}
          >
            <div style={{ flex: 1 }}>
              <h3 className="agent-conv-h" style={{ fontSize: 14, margin: 0 }}>
                Speaker boost
              </h3>
              <p className="agent-limits-lead" style={{ margin: "4px 0 0" }}>
                ElevenLabs clarity enhancement (default on).
              </p>
            </div>
            <Switch
              checked={draft.liveAvatarVoiceSpeakerBoost !== false}
              onCheckedChange={(on) => patch({ liveAvatarVoiceSpeakerBoost: on })}
              aria-label="ElevenLabs speaker boost"
            />
          </div>
        </section>

        <section className="agent-voice-selected-card">
          <div className="agent-voice-selected-row">
            <VoicePlayButton
              variant="solid"
              playState={selectedId ? selectedPlayState : "idle"}
              disabled={!selectedId}
              aria-label={
                !selectedId
                  ? "Select a voice to preview"
                  : selectedPlayState === "playing"
                    ? "Pause voice preview"
                    : "Play voice preview"
              }
              onClick={() => {
                if (selectedId) void player.playElevenLabsPreview(selectedId);
              }}
            />
            <div className="agent-voice-selected-copy">
              <b>{displayName}</b>
              <p>{displayDesc}</p>
              {selectedId ? (
                <code className="agent-voice-id">{selectedId}</code>
              ) : (
                <span className="agent-voice-id-muted">No ElevenLabs voice selected</span>
              )}
            </div>
          </div>
          {selectedId ? (
            <div className="agent-voice-clear-wrap">
              <button
                type="button"
                className="agent-voice-clear"
                onClick={() =>
                  patch({ elevenLabsVoiceId: null, liveAvatarVoiceId: null })
                }
              >
                Clear selection
              </button>
              <p className="agent-voice-clear-hint">
                Clear before Save if you need to pick another voice.
              </p>
            </div>
          ) : null}
        </section>

        {(player.error && player.activeKey?.startsWith("el:")) || listError ? (
          <p className="agent-voice-error" role="alert">
            {player.error || listError}
          </p>
        ) : null}

        {draft.liveAvatarVoiceId ? (
          <p className="agent-limits-lead" style={{ marginTop: 12 }}>
            LiveAvatar voice id: <code>{draft.liveAvatarVoiceId}</code>
          </p>
        ) : null}

        <div className="agent-limits-note" role="note" style={{ marginTop: 16 }}>
          <Info size={15} strokeWidth={2} aria-hidden />
          <p>
            No End of turn, idle tools, or hangup tools on this mode. Video avatar must stay
            enabled with a ready LiveAvatar bind.
          </p>
        </div>
      </div>

      {libraryOpen && (
        <aside className="agent-voice-library" aria-label="ElevenLabs voice library">
          <header className="agent-voice-library-head">
            <div>
              <b>Choose a voice</b>
              <span>Browse ElevenLabs voices and preview before selecting.</span>
            </div>
            <button
              type="button"
              className="agent-edit-close"
              aria-label="Close library"
              onClick={() => setLibraryOpen(false)}
            >
              <X size={16} />
            </button>
          </header>

          <div className="agent-voice-filters">
            <label className="agent-voice-search">
              <Search size={14} aria-hidden />
              <input
                className="agent-conv-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or ID…"
                aria-label="Search voices"
              />
            </label>
          </div>

          {listError ? <p className="agent-voice-error" role="alert">{listError}</p> : null}

          <div className="agent-voice-list-wrap">
            {loading && voices.length === 0 ? (
              <div className="agent-voice-empty">
                <Loader2 className="spin" size={18} /> Loading voices…
              </div>
            ) : voices.length === 0 ? (
              <div className="agent-voice-empty">
                No voices found. Add an ElevenLabs API key under Settings → Credentials.
              </div>
            ) : filtered.length === 0 ? (
              <div className="agent-voice-empty">No voices match.</div>
            ) : (
              <ul className="agent-voice-list" role="listbox" aria-label="Voices">
                {filtered.map((v) => {
                  const active = v.voiceId === selectedId;
                  const playKey = `el:${v.voiceId}`;
                  const playState = player.stateFor(playKey);
                  const title = voiceTitle(v);
                  return (
                    <li
                      key={v.voiceId}
                      role="option"
                      aria-selected={active}
                      className={`agent-voice-row${active ? " is-active" : ""}`}
                    >
                      <VoicePlayButton
                        variant="outline"
                        playState={playState}
                        onClick={() => void player.playElevenLabsPreview(v.voiceId)}
                        aria-label={`Play preview of ${title}`}
                      />
                      <button
                        type="button"
                        className="agent-voice-row-copy"
                        onClick={() =>
                          patch({
                            elevenLabsVoiceId: v.voiceId,
                            liveAvatarVoiceId: null,
                          })
                        }
                      >
                        <span className="agent-voice-row-name">{title}</span>
                        <code className="agent-voice-id">{v.voiceId}</code>
                      </button>
                      <input
                        type="radio"
                        name="agent-liveavatar-voice-pick"
                        checked={active}
                        onChange={() =>
                          patch({
                            elevenLabsVoiceId: v.voiceId,
                            liveAvatarVoiceId: null,
                          })
                        }
                        aria-label={`Select ${title}`}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
